<script lang="ts">
  import { onMount } from 'svelte';
  import { currentTheme as theme, palette as paletteConst } from './theme.svelte';
  import Sidebar from './lib/layout/Sidebar.svelte';
  import ChatLayout from './lib/layout/ChatLayout.svelte';
  import BibliotecaPage from './features/biblioteca/BibliotecaPage.svelte';
  import SeguimientoJudicialPage from './features/seguimiento-judicial/SeguimientoJudicialPage.svelte';
  import { getRoute, initRouter } from './route.svelte';
  import { globalNotification } from './errorBanner.svelte';
  import { initializeAuth, refreshSessionInfo, setAvailableMessages } from './auth.svelte';
  import { subscribeCredits } from './lib/ws';

  const BREAKPOINT = 768;
  let isSidebarCollapsed = $state(false);
  let isMobile = $state(false);
  let palette = $derived(paletteConst[theme.value]);
  let route = $derived(getRoute());

  function updateIsMobile() {
    isMobile = window.innerWidth < BREAKPOINT;
  }

  function toggleSidebar() {
    isSidebarCollapsed = !isSidebarCollapsed;
  }

  onMount(() => {
    initRouter();
    void initializeAuth();
    updateIsMobile();
    if (isMobile) {
      isSidebarCollapsed = true;
    }
    const handler = () => updateIsMobile();
    window.addEventListener('resize', handler);

    // ── Créditos ───────────────────────────────────────────
    // El backend empuja el saldo tras cada descuento o reintegro; el resto de
    // cambios (compras, planes activados en otra pestaña) se reconcilian con
    // /session/ al recuperar el foco, sin sobrecargar la API.
    const unsubscribeCredits = subscribeCredits((remaining) => setAvailableMessages(remaining));

    let lastSyncAt = 0;
    const syncCredits = () => {
      if (document.visibilityState === 'hidden') return;
      const now = Date.now();
      if (now - lastSyncAt < 5000) return;
      lastSyncAt = now;
      void refreshSessionInfo();
    };
    window.addEventListener('focus', syncCredits);
    document.addEventListener('visibilitychange', syncCredits);

    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('focus', syncCredits);
      document.removeEventListener('visibilitychange', syncCredits);
      unsubscribeCredits();
    };
  });

  $effect(() => {
    if (isMobile && !isSidebarCollapsed) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'hidden';
    }
  });
</script>

<svelte:head>
  <title>Jurismart | Chat Web</title>
</svelte:head>

<div
  class="app-shell {isMobile ? 'mobile' : 'desktop'}"
  style={`background-color: ${palette.bgPrimary}; color: ${palette.textPrimary}`}
>
  {#if globalNotification.message != ''}
    <div class="global-notification-banner {globalNotification.type}">{globalNotification.message}</div>
  {/if}
  <Sidebar
    isCollapsed={isSidebarCollapsed}
    {palette}
    {isMobile}
    onCloseSidebar={toggleSidebar}
  />

  {#if route.name === 'biblioteca' || route.name === 'biblioteca-document'}
    <BibliotecaPage
      onToggleSidebar={toggleSidebar}
      {palette}
      {isSidebarCollapsed}
      {isMobile}
    />
  {:else if route.name === 'seguimiento' || route.name === 'seguimiento-detalle'}
    <SeguimientoJudicialPage
      onToggleSidebar={toggleSidebar}
      {palette}
      {isSidebarCollapsed}
      {isMobile}
    />
  {:else}
    <ChatLayout
      onToggleSidebar={toggleSidebar}
      {palette}
      {isSidebarCollapsed}
      {isMobile}
    />
  {/if}
</div>

<style>
  .app-shell {
    display: flex;
    height: 100%;
    width: 100%;
    min-width: 0;
    min-height: 0;
    max-width: 100vw;
    overflow: hidden;
    position: relative;
  }

  .app-shell.mobile {
    height: 100vh;
    height: 100dvh;
  }

  .global-notification-banner {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    background: rgba(220, 38, 38, 0.95); /* error red by default */
    color: #fff;
    padding: 8px 16px;
    font-size: 13px;
    text-align: center;
    z-index: 100;
    animation: fadeout 4s forwards;
  }

  .global-notification-banner.success {
    background: rgba(34, 197, 94, 0.95); /* #22c55e green */
  }

  @keyframes fadeout {
    0% { opacity: 1; }
    70% { opacity: 1; }
    100% { opacity: 0; }
  }
</style>
